import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles } from "lucide-react";
import PlanFeatureGroups from "./PlanFeatureGroups";
import { getIncludedPlanFeatures } from "./planFeatureUtils";
import { getTierIconComponent } from "./tierIcons";

const formatMonthlyCredit = (value) => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return `${amount.toLocaleString(undefined, {
    maximumFractionDigits: 4,
  })} AI credits / month`;
};

/**
 * A single subscription tier card showing name, description, features,
 * and an action button (Current / Upgrade / Downgrade / Get Started).
 */
export default function TierCard({
  tier,
  currentTierCode,
  currentTierSort,
  onSelect,
  loading,
}) {
  const isCurrent = tier.code === currentTierCode;
  const isUpgrade = tier.sort_order > (currentTierSort ?? -1);
  const _isDowngrade = tier.sort_order < (currentTierSort ?? -1);
  const includedFeatures = getIncludedPlanFeatures(tier);
  const priceLabel = tier.price_label ?? (tier.is_free ? "Free" : null);
  const monthlyCreditLabel = formatMonthlyCredit(tier.monthly_credit);
  const TierIcon = getTierIconComponent(tier.icon);

  const getActionLabel = () => {
    if (isCurrent) return "Current Plan";
    if (!currentTierCode) return "Get Started";
    if (isUpgrade) return "Upgrade";
    return "Downgrade";
  };

  const getButtonVariant = () => {
    if (isCurrent) return "outline";
    if (isUpgrade) return "default";
    return "secondary";
  };

  return (
    <Card
      className={`relative flex flex-col h-full transition-shadow hover:shadow-lg ${
        isCurrent
          ? "ring-2 ring-primary border-primary"
          : "border-borderDefault"
      }`}
    >
      {/* Header */}
      <div className="p-5 pb-3">
        <div className="flex items-center gap-2 mb-1">
          <TierIcon className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-textPrimary">{tier.name}</h3>
          {isCurrent && <Badge variant="success">Active</Badge>}
        </div>
        {tier.description && (
          <p className="text-sm text-textSecondary mt-1">{tier.description}</p>
        )}

        {/* Price */}
        <div className="mt-3">
          {priceLabel ? (
            <div>
              <p className="text-2xl font-bold text-textPrimary">
                {priceLabel}
              </p>
              {tier.yearly_price_label && (
                <p className="text-xs text-textSecondary mt-0.5">
                  or {tier.yearly_price_label}{" "}
                  {tier.yearly_savings_percent != null && (
                    <span className="text-success font-medium">
                      (save {tier.yearly_savings_percent}%)
                    </span>
                  )}
                </p>
              )}
              {monthlyCreditLabel && (
                <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-primary">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>{monthlyCreditLabel}</span>
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-textSecondary italic">Price not set</p>
          )}
        </div>
      </div>

      {/* Features */}
      <CardContent className="flex-1 px-5 pb-2">
        <h4 className="text-xs font-semibold uppercase text-textSecondary tracking-wider mb-3">
          Features
        </h4>
        <PlanFeatureGroups features={includedFeatures} />
      </CardContent>

      {/* Action button */}
      <div className="p-5 pt-3 mt-auto">
        <Button
          variant={getButtonVariant()}
          size="md"
          className="w-full"
          disabled={isCurrent || loading}
          onClick={() => !isCurrent && onSelect?.(tier)}
        >
          {loading ? "Processing..." : getActionLabel()}
        </Button>
      </div>
    </Card>
  );
}
